import React, { useState, useEffect } from 'react';
import { X, User, Save, Check } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import toast from 'react-hot-toast';

/**
 * EditProfileModal - Allows users to update their display name
 */
const EditProfileModal = ({ 
  isOpen, 
  onClose, 
  user, 
  currentGroup,
  onUpdateMemberName 
}) => {
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Load current display name
  useEffect(() => {
    if (!isOpen || !user) return;

    const loadDisplayName = async () => {
      try {
        // Try to get from user document
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists() && userDoc.data().displayName) {
          setDisplayName(userDoc.data().displayName);
        } else if (user.displayName) {
          setDisplayName(user.displayName);
        } else if (currentGroup?.members?.[user.uid]?.name) {
          setDisplayName(currentGroup.members[user.uid].name);
        } else {
          setDisplayName(user.email?.split('@')[0] || '');
        }
      } catch (err) {
        console.error('Error loading display name:', err);
        setDisplayName(user.email?.split('@')[0] || '');
      }
    };

    loadDisplayName();
    setIsSaved(false);
  }, [isOpen, user, currentGroup]);

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }

    setIsLoading(true);
    try {
      // Update user document
      const userDocRef = doc(db, 'users', user.uid);
      await setDoc(userDocRef, {
        displayName: displayName.trim(),
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Update in current group's members (if available)
      if (onUpdateMemberName) {
        await onUpdateMemberName(displayName.trim());
      }

      setIsSaved(true);
      toast.success('Display name updated!');
      
      // Close after brief delay to show success state
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Error saving display name:', err);
      toast.error('Failed to update display name');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-xl">
              <User className="text-purple-600 dark:text-purple-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Edit Profile</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Update your display name</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Display Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  setIsSaved(false);
                }}
                placeholder="Enter your name"
                className="w-full pl-11 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                maxLength={30}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              This is how you'll appear to your bandmates in polls and activity.
            </p>
          </div>

          {/* Current email (read-only info) */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email</p>
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              {user?.email}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !displayName.trim() || isSaved}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                isSaved
                  ? 'bg-green-500 text-white'
                  : isLoading || !displayName.trim()
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : isSaved ? (
                <>
                  <Check size={18} />
                  Saved!
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;